import { useMemo } from 'react'

import { useAppContext } from 'contexts/app-context/hooks/useAppContext'
import { TEndpoint } from 'contexts/app-context/types'
import { HTTP_STATUS } from 'common/constants'

import { useQuery } from '@tanstack/react-query'

import { IRequestTermsRequest } from '../types/useRequestTerms.types'
import { getRequestTermsBody } from '../utils'
import { getRequestTermsQueryOptions } from './queries/getRequestTermsQueryOptions'

const useRequestTerms = () => {
  const { privacyCenterContent, userSelectedEntity, userEntities, userCountry } = useAppContext()

  const endpoint: TEndpoint | undefined = useMemo(() => {
    const requestPrivacyPoliciesEndpoint =
      privacyCenterContent?.endpoints?.privacyPolicy?.requestPrivacyPolicies

    if (!requestPrivacyPoliciesEndpoint) return undefined

    return requestPrivacyPoliciesEndpoint
  }, [privacyCenterContent?.endpoints?.privacyPolicy?.requestPrivacyPolicies])

  const requestBody: IRequestTermsRequest | undefined = useMemo(() => {
    if (!userSelectedEntity || !userCountry || !userEntities) return undefined

    return getRequestTermsBody({
      org: userCountry,
      userSelectedEntity,
      userEntities,
    })
  }, [userCountry, userEntities, userSelectedEntity])

  const {
    data: terms,
    status,
    isLoading,
    error,
  } = useQuery(
    getRequestTermsQueryOptions({
      endpoint,
      requestBody,
    }),
  )

  const errorValue =
    error &&
    (JSON.parse(error as unknown as string) as {
      action: string
      message: string
      httpStatus: number
      response: string
    })

  const notFoundTerms = !!(
    errorValue?.httpStatus && errorValue.httpStatus.toString() === HTTP_STATUS.NOT_FOUND
  )

  const dataValue = useMemo(() => {
    if (notFoundTerms || !terms) return []
    return terms
  }, [notFoundTerms, terms])

  return { terms: dataValue, status, isLoading, notFoundTerms }
}

export { useRequestTerms }
